import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { GasNotification } from './gas-notify.namespace';
import { GasMathHelper } from '@cikrf/gas-utils/helpers';

// todo: Добавить интерфейс сервиса, разнести работу нотификаций и тостов в разные сервисы и сделать возможность подключать по необходимости

/**
 * @description Сервис для управления нотификациями
 * @example
 * this.gasNotificationService.pushNotifications([{
 *   title: 'Delete init',
 *   type: 'warning',
 * }]
 */
@Injectable({
  providedIn: 'root',
})
export class GasNotificationService {
  public notifications: GasNotification.Notification[] = [];

  public toastNotifications: GasNotification.Notification[] = [];

  private notificationsSubject$ = new BehaviorSubject<GasNotification.Notification[]>([]);

  private toastNotificationsSubject$ = new BehaviorSubject<GasNotification.Notification[]>([]);


  /**
   * @description Возвращает observable GasNotification.Notification[] возвращает уникальные нотификации
   */
  public get notifications$(): Observable<GasNotification.Notification[]> {
    return this.notificationsSubject$.pipe(filterDeletedNotifications());
  }

  /**
   * @description Возвращает observable GasNotification.Notification[] возвращает уникальные toast-нотификации
   */
  public get toastNotifications$(): Observable<GasNotification.Notification[]> {
    return this.toastNotificationsSubject$.pipe(filterDeletedNotifications());
  }

  constructor() {
    this.notifications$.subscribe((notifications: GasNotification.Notification[]) => {
      this.notifications = notifications;
    });

    this.toastNotifications$.subscribe((toasts: GasNotification.Notification[]) => {
      this.toastNotifications = toasts;
    });
  }

  /**
   * @description Установка новых нотификаций, принимает массив нотификаций
   */
  public pushNotifications(notifications: GasNotification.Notification[]): void {
    notifications = notifications.map(n => n.id ? n : { ...n, id: GasMathHelper.getUniqueId() });
    notifications.filter(nn => this.toastNotifications.some(ol => ol.id === nn.id));

    this.notificationsSubject$.next([...this.notifications, ...notifications]);
  }

  /**
   * @description Добавляет новую нотификацию и возвращает ее уникальный id
   */
  public pushNotification(notification: GasNotification.Notification): string {
    if (!notification.id) {
      notification.id = GasMathHelper.getUniqueId();
    }
    const oldNotifications = this.notifications.filter(n => n.id !== notification.id);

    this.notificationsSubject$.next([...oldNotifications, notification]);
    return notification.id;
  }

  /**
   * @description Установка новых toast-нотификаций, принимает массив нотификаций
   */
  public pushToastNotifications(notifications: GasNotification.Notification[]): void {
    notifications = notifications.map(n => n.id ? n : { ...n, id: GasMathHelper.getUniqueId() });
    notifications.filter(nn => this.toastNotifications.some(ol => ol.id === nn.id));

    this.toastNotificationsSubject$.next([...this.toastNotifications, ...notifications]);
  }

  /**
   * @description Добавляет новую toast-нотификацию и возвращает ее уникальный id
   */
  public pushToastNotification(notification: GasNotification.Notification): string {
    if (!notification.id) {
      notification.id = GasMathHelper.getUniqueId();
    }
    const oldNotifications = this.toastNotifications.filter(n => n.id !== notification.id);

    this.toastNotificationsSubject$.next([...oldNotifications, notification]);
    return notification.id;
  }

  /**
   * @description Отправка нотификации
   */
  public send(type: GasNotification.Type, title: string, link?: string, linkCallback?: () => void): string {
    return this.pushNotification({ type, title, link, linkCallback });
  }

  /**
   * @description Отправка нотификации об ошибке
   */
  public sendError(title: string, link?: string, linkCallback?: () => void): string {
    return this.pushNotification({ type: GasNotification.Type.Error, title, link, linkCallback });
  }

  /**
   * @description Отправка информационной нотификации
   */
  public sendGeneral(title: string, link?: string, linkCallback?: () => void): string {
    return this.pushNotification({ type: GasNotification.Type.General, title, link, linkCallback });
  }

  /**
   * @description Отправка нотификации о предупреждении
   */
  public sendWarning(title: string, link?: string, linkCallback?: () => void): string {
    return this.pushNotification({ type: GasNotification.Type.Warning, title, link, linkCallback });
  }

  /**
   * @description Отправка нотификации об успехе
   */
  public sendSuccess(title: string, link?: string, linkCallback?: () => void): string {
    return this.pushNotification({ type: GasNotification.Type.Success, title, link, linkCallback });
  }

  /**
   * @description Отправка светлой информационной нотификации
   */
  public sendLightGeneral(title: string, link?: string, linkCallback?: () => void): string {
    return this.pushNotification({ type: GasNotification.Type.LightGeneral, title, link, linkCallback });
  }

  /**
   * @description Отправка светлой нотификации об ошибке
   */
  public sendLightError(title: string, link?: string, linkCallback?: () => void): string {
    return this.pushNotification({ type: GasNotification.Type.LightError, title, link, linkCallback });
  }

  /**
   * @description Отправка светлой нотификации о предупреждении
   */
  public sendLightWarning(title: string, link?: string, linkCallback?: () => void): string {
    return this.pushNotification({ type: GasNotification.Type.LightWarning, title, link, linkCallback });
  }

  /**
   * @description Отправка светлой нотификации об успехе
   */
  public sendLightSuccess(title: string, link?: string, linkCallback?: () => void): string {
    return this.pushNotification({ type: GasNotification.Type.LightSuccess, title, link, linkCallback });
  }

  /**
   * @description Удаление конкретной нотификации
   */
  public closeNotification(id: string): void {
    this.notificationsSubject$.next(
      this.notifications.map((notification: GasNotification.Notification) =>
        notification.id === id ? { ...notification, deleted: true } : notification,
      ),
    );
  }

  /**
   * @description Удаление конкретной toast-нотификации
   */
  public closeToastNotification(id: string): void {
    this.toastNotificationsSubject$.next(
      this.toastNotifications.map((notification: GasNotification.Notification) =>
        notification.id === id ? { ...notification, deleted: true } : notification,
      ),
    );
  }
}

function filterDeletedNotifications() {
  return (source: Observable<GasNotification.Notification[]>): Observable<GasNotification.Notification[]> => {
    return source.pipe(
      map((notifications: GasNotification.Notification[]) => notifications.filter(n => !n.deleted)),
    );
  };
}
